import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { amountConversion } from "utils/utils";
import Card from "components/Card/Card";
import s from "./StatisticsOfflineMerchant.module.scss";
import AnalyticsTable from "components/AnalyticsTable/AnalyticsTable";
import TextMAS from "components/TextMAS/TextMAS";
import { fetchStatistics } from "./model";
import { useAuth } from "utils/useAuth/useAuth";
import PageContainer from "components/PageContainer/PageContainer";

const PAGE_LOCALE_KEY = "page.analytics.systemStatistics.offlineMerchant.";
const NO_DATA_PLACEHOLDER = "-";

const StatisticsOfflineMerchant = ({ focus }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [error, setError] = useState();
  const [hasFetched, setHasFetched] = useState(false);

  const [dataSource, setDataSource] = useState({
    yearlyStats: [],
    monthlyStats: [],
  });

  useEffect(() => {
    if (focus && !hasFetched) {
      getStatistics();
      setHasFetched(true);
    }
  }, [focus]);

  const getStatistics = async () => {
    setIsLoading(true);
    const response = await fetchStatistics();

    if (!response) {
      setError("networkError");
      setIsLoading(false);
      return;
    }

    switch (response?.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        setIsUnauthorized(true);
        setIsLoading(false);
        return;
      }
      default: {
        setError(true);
        setIsLoading(false);
        return;
      }
      case 200:
        break;
    }

    const result = response?.data;
    switch (result?.responseCode) {
      case "0000":
        setDataSource(result.data);
        break;
      case "1200":
        setIsUnauthorized(true);
        break;
      default:
        setError(t(PAGE_LOCALE_KEY + "fetchingError.merchantStat"));
        break;
    }
    setIsLoading(false);
  };

  const renderMerchantChangeCell = (count, countChange, changePercentage) => {
    const statChangeColorClass = !countChange
      ? s["change-stat-grey"]
      : countChange > 0
        ? s["change-stat-green"]
        : s["change-stat-red"];

    const amountSymbol = !countChange ? "" : countChange > 0 ? "+" : "-";

    return (
      <div className={s["change-cell-container"]}>
        <TextMAS fontSize="18px">{amountConversion(count)}</TextMAS>
        <div
          className={`${s["change-stat-container"]} ${statChangeColorClass}`}
        >
          <TextMAS fontSize="small" className={statChangeColorClass}>
            {amountSymbol + amountConversion(Math.abs(countChange))}
          </TextMAS>
          &nbsp;
          <TextMAS fontSize="small" className={statChangeColorClass}>
            {"(" + (changePercentage ?? "--") + "%)"}
          </TextMAS>
        </div>
      </div>
    );
  };

  const merchantYearChangeColumns = [
    {
      id: "year",
      title: t(PAGE_LOCALE_KEY + "merchantYearChange.columnTitle.1"),
      key: "date",
      headerStyle: { width: "160px" },
      //sorting: true,
      sortingKey: ({ data, key }) => {
        return moment(data[key]).unix();
      },
    },
    {
      id: "total",
      title: t(PAGE_LOCALE_KEY + "merchantYearChange.columnTitle.2"),
      key: "total",
      sortingKey: "totalCount",
      //sorting: true,
      render: ({ data }) => {
        return renderMerchantChangeCell(
          data.totalCount,
          data.totalCountChange,
          data.totalCountChangePercentage,
        );
      },
    },
    {
      id: "active",
      title: t(PAGE_LOCALE_KEY + "merchantYearChange.columnTitle.active"),
      key: "active",
      //sorting: true,
      sortingKey: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return item.count;
      },
      render: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return renderMerchantChangeCell(
          item?.count,
          item?.countChange,
          item?.changePercentage,
        );
      },
    },
    {
      id: "suspended",
      title: t(PAGE_LOCALE_KEY + "merchantYearChange.columnTitle.suspended"),
      key: "suspended",
      //sorting: true,
      sortingKey: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return item.count;
      },
      render: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return renderMerchantChangeCell(
          item?.count,
          item?.countChange,
          item?.changePercentage,
        );
      },
    },
    {
      id: "terminated",
      title: t(PAGE_LOCALE_KEY + "merchantYearChange.columnTitle.terminated"),
      key: "terminated",
      //sorting: true,
      sortingKey: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return item.count;
      },
      render: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return renderMerchantChangeCell(
          item?.count,
          item?.countChange,
          item?.changePercentage,
        );
      },
    },
  ];

  const merchantMonthChangeColumns = [
    {
      id: "year",
      title: t(PAGE_LOCALE_KEY + "merchantMonthChange.columnTitle.1"),
      key: "date",
      headerStyle: { width: "160px" },
      //sorting: true,
      sortingKey: ({ data, key }) => {
        return moment(data[key]).unix();
      },
    },
    {
      id: "total",
      title: t(PAGE_LOCALE_KEY + "merchantMonthChange.columnTitle.2"),
      key: "total",
      //sorting: true,
      sortingKey: "totalCount",
      render: ({ data }) => {
        return renderMerchantChangeCell(
          data.totalCount,
          data.totalCountChange,
          data.totalCountChangePercentage,
        );
      },
    },
    {
      id: "active",
      title: t(PAGE_LOCALE_KEY + "merchantMonthChange.columnTitle.active"),
      key: "active",
      //sorting: true,
      sortingKey: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return item.count;
      },
      render: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return renderMerchantChangeCell(
          item?.count,
          item?.countChange,
          item?.changePercentage,
        );
      },
    },
    {
      id: "suspended",
      title: t(PAGE_LOCALE_KEY + "merchantMonthChange.columnTitle.suspended"),
      key: "suspended",
      //sorting: true,
      sortingKey: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return item.count;
      },
      render: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return renderMerchantChangeCell(
          item?.count,
          item?.countChange,
          item?.changePercentage,
        );
      },
    },
    {
      id: "terminated",
      title: t(PAGE_LOCALE_KEY + "merchantMonthChange.columnTitle.terminated"),
      key: "terminated",
      //sorting: true,
      sortingKey: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return item.count;
      },
      render: ({ data, key }) => {
        const item = data?.breakdown?.find((item) => item.status === key);
        return renderMerchantChangeCell(
          item?.count,
          item?.countChange,
          item?.changePercentage,
        );
      },
    },
  ];

  return (
    <PageContainer
      loading={isLoading}
      unauthorized={isUnauthorized}
      errorMessage={error}
    >
      <div className={s["analytics-merchant-offline-container"]}>
        <div className={s["last-modified-container"]}>
          <TextMAS>
            {t(PAGE_LOCALE_KEY + "lastUpdated") + ": "}
            {dataSource?.latestUpdate
              ? moment(dataSource?.latestUpdate).format("YYYY-MM-DD HH:mm")
              : NO_DATA_PLACEHOLDER}
          </TextMAS>
        </div>

        <div className={s["merchant-info-card-container"]}>
          <Card>
            <div className={s["merchant-info-vertical-container"]}>
              <TextMAS className={s["merchant-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.activeMerchMonth.title")}
              </TextMAS>
              <TextMAS className={s["merchant-info-card-amount"]}>
                {amountConversion(
                  dataSource?.monthlyStats?.[0]?.breakdown?.find(
                    (e) => e.status === "active",
                  )?.count,
                ) ?? NO_DATA_PLACEHOLDER}
              </TextMAS>
            </div>
          </Card>
          <Card>
            <div className={s["merchant-info-vertical-container"]}>
              <TextMAS className={s["merchant-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.activeMerchYear.title")}
              </TextMAS>
              <TextMAS className={s["merchant-info-card-amount"]}>
                {amountConversion(
                  dataSource?.yearlyStats?.[0]?.breakdown?.find(
                    (e) => e.status === "active",
                  )?.count,
                ) ?? NO_DATA_PLACEHOLDER}
              </TextMAS>
            </div>
          </Card>
          <Card>
            <div className={s["merchant-info-vertical-container"]}>
              <TextMAS className={s["merchant-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.sme.title")}
              </TextMAS>
              <TextMAS className={s["merchant-info-card-amount"]}>
                {amountConversion(
                  dataSource?.specialMerchantStats?.totalSmePlanMerchant,
                ) ?? NO_DATA_PLACEHOLDER}
              </TextMAS>
            </div>
          </Card>
          <Card>
            <div className={s["merchant-info-vertical-container"]}>
              <TextMAS className={s["merchant-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.zeroPlan.title")}
              </TextMAS>
              <TextMAS className={s["merchant-info-card-amount"]}>
                {amountConversion(
                  dataSource?.specialMerchantStats?.totalZeroPlanMerchant,
                ) ?? NO_DATA_PLACEHOLDER}
              </TextMAS>
            </div>
          </Card>
          <Card>
            <div className={s["merchant-info-horizontal-container"]}>
              <div className={s["merchant-info-vertical-container"]}>
                <TextMAS className={s["merchant-info-card-title"]}>
                  {t(PAGE_LOCALE_KEY + "infoCard.boxsPay.title")}
                </TextMAS>
                <TextMAS className={s["merchant-info-card-amount"]}>
                  {amountConversion(
                    dataSource?.specialMerchantStats?.totalActiveBoxsMerchant,
                  ) ?? NO_DATA_PLACEHOLDER}
                </TextMAS>
              </div>
              <div className={s["merchant-info-vertical-container"]}></div>
            </div>
          </Card>
        </div>

        <div className={s["merchant-year-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "merchantYearChange.title")}
          </TextMAS>
          <AnalyticsTable
            columns={merchantYearChangeColumns}
            data={dataSource?.yearlyStats}
            tableStyle={{ minWidth: "1150px", maxWidth: "1500px" }}
          />
        </div>
        <div className={s["merchant-month-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "merchantMonthChange.title")}
          </TextMAS>
          <AnalyticsTable
            columns={merchantMonthChangeColumns}
            data={dataSource?.monthlyStats}
            tableStyle={{ minWidth: "1150px", maxWidth: "1500px" }}
          />
        </div>
        <div>
          <TextMAS
            fontSize="small"
            lineBreak
            text={t(PAGE_LOCALE_KEY + "notice.content.1")}
          />
          <TextMAS
            fontSize="small"
            lineBreak
            text={t(PAGE_LOCALE_KEY + "notice.content.2")}
          />
          <TextMAS
            fontSize="small"
            lineBreak
            text={t(PAGE_LOCALE_KEY + "notice.content.3")}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default React.memo(StatisticsOfflineMerchant);
